import gql from 'graphql-tag';

import {
  facetsFragment,
  productFragment,
  productSearchResult
} from '../../../shared/graphql/product-list-queries';

import {
  PACKAGE_PAGE_SIZE,
  PAGE_SIZE
} from '../../../shared/constants/products';

const TIRE_PACKAGE_SEARCH = gql`
  query tirePackageSearch(
    $searchInput: TirePackageSearchInput!
    $vehicleInfo: VehicleInput!
    $storeCode: String!
  ) {
    fitmentSearch(vehicleInfo: $vehicleInfo) {
      tirePackageSearch(searchInput: $searchInput, storeCode: $storeCode) {
        ...productSearchResult
      }
    }
  }
  ${productSearchResult}
`;

export const getTirePackageSearch = ({
  frontTireSize,
  frontWheelProductCode,
  nearByStoreCodes,
  pageNum,
  rearWheelProductCode,
  searchOption,
  searchQuery,
  staggeredState = 'OFF',
  storeCode,
  treadwell,
  vehicleInput
}) => ({
  mapResponse: data => ({
    filters: data?.fitmentSearch?.tirePackageSearch?.facets,
    numberOfPages:
      data?.fitmentSearch?.tirePackageSearch?.pagination?.numberOfPages,
    products: data?.fitmentSearch?.tirePackageSearch?.results,
    recommendationAvailable:
      data?.fitmentSearch?.tirePackageSearch?.recommendationAvailable,
    sorts: data?.fitmentSearch?.tirePackageSearch?.sorts,
    totalNumberOfResults:
      data?.fitmentSearch?.tirePackageSearch?.pagination?.totalNumberOfResults
  }),
  query: TIRE_PACKAGE_SEARCH,
  variables: {
    searchInput: {
      frontProductCode: frontWheelProductCode,
      nearByStoreCodes,
      packageType: 'TIREPACKAGES',
      pageNumber: pageNum,
      pageSize: PAGE_SIZE,
      rawQuery: searchQuery,
      rearProductCode: rearWheelProductCode,
      searchOption,
      size: frontTireSize,
      staggeredState,
      treadwell
    },
    storeCode,
    vehicleInfo: vehicleInput
  }
});

const STAGGERED_TIRE_PACKAGE_SEARCH = gql`
  query staggeredTirePackageSearch(
    $searchInput: StaggeredTirePackageSearchInput!
    $vehicleInfo: VehicleInput!
    $storeCode: String!
  ) {
    fitmentSearch(vehicleInfo: $vehicleInfo) {
      staggeredTirePackageSearch(
        searchInput: $searchInput
        storeCode: $storeCode
      ) {
        ...productSearchResult
      }
    }
  }
  ${productSearchResult}
`;

export const getStaggeredTirePackageSearch = ({
  frontTireSize,
  frontWheelProductCode,
  nearByStoreCodes,
  pageNum,
  rearTireSize,
  rearWheelProductCode,
  searchOption,
  searchQuery,
  storeCode,
  treadwell,
  vehicleInput
}) => ({
  errorPolicy: 'all',
  mapResponse: data => ({
    filters: data?.fitmentSearch?.staggeredTirePackageSearch?.facets,
    numberOfPages:
      data?.fitmentSearch?.staggeredTirePackageSearch?.pagination
        ?.numberOfPages,
    products: data?.fitmentSearch?.staggeredTirePackageSearch?.results,
    recommendationAvailable:
      data?.fitmentSearch?.staggeredTirePackageSearch?.recommendationAvailable,
    sorts: data?.fitmentSearch?.staggeredTirePackageSearch?.sorts,
    totalNumberOfResults:
      data?.fitmentSearch?.staggeredTirePackageSearch?.pagination
        ?.totalNumberOfResults
  }),
  query: STAGGERED_TIRE_PACKAGE_SEARCH,
  variables: {
    searchInput: {
      front: frontTireSize,
      frontWheelProductCode,
      nearByStoreCodes,
      pageNumber: pageNum,
      pageSize: PAGE_SIZE,
      rawQuery: searchQuery,
      rear: rearTireSize,
      rearWheelProductCode,
      searchOption,
      treadwell
    },
    storeCode,
    vehicleInfo: vehicleInput
  }
});

const WHEEL_PACKAGE_SEARCH = gql`
  query wheelPackageSearch(
    $searchInput: WheelPackageSearchInput!
    $vehicleInfo: VehicleInput!
    $storeCode: String!
  ) {
    fitmentSearch(vehicleInfo: $vehicleInfo) {
      wheelPackageSearch(searchInput: $searchInput, storeCode: $storeCode) {
        ...productSearchResult
      }
    }
  }
  ${productSearchResult}
`;

export const getWheelPackageSearch = ({
  frontTireProductCode,
  frontWheelSize,
  nearByStoreCodes,
  pageNum,
  rearTireProductCode,
  searchOption,
  searchQuery,
  staggeredState = 'OFF',
  storeCode,
  vehicleInput
}) => ({
  errorPolicy: 'all',
  mapResponse: data => ({
    filters: data?.fitmentSearch?.wheelPackageSearch?.facets,
    numberOfPages:
      data?.fitmentSearch?.wheelPackageSearch?.pagination?.numberOfPages,
    products: data?.fitmentSearch?.wheelPackageSearch?.results,
    sorts: data?.fitmentSearch?.wheelPackageSearch?.sorts,
    totalNumberOfResults:
      data?.fitmentSearch?.wheelPackageSearch?.pagination?.totalNumberOfResults
  }),
  query: WHEEL_PACKAGE_SEARCH,
  variables: {
    searchInput: {
      frontProductCode: frontTireProductCode,
      nearByStoreCodes,
      packageType: 'WHEELPACKAGES',
      pageNumber: pageNum,
      pageSize: PAGE_SIZE,
      rawQuery: searchQuery,
      rearProductCode: rearTireProductCode,
      searchOption,
      size: frontWheelSize,
      staggeredState
    },
    storeCode,
    vehicleInfo: vehicleInput
  }
});

const STAGGERED_WHEEL_PACKAGE_SEARCH = gql`
  query staggeredWheelPackageSearch(
    $searchInput: StaggeredWheelPackageSearchInput!
    $vehicleInfo: VehicleInput!
    $storeCode: String!
  ) {
    fitmentSearch(vehicleInfo: $vehicleInfo) {
      staggeredWheelPackageSearch(
        searchInput: $searchInput
        storeCode: $storeCode
      ) {
        ...productSearchResult
      }
    }
  }
  ${productSearchResult}
`;

export const getStaggeredWheelPackageSearch = ({
  frontTireProductCode,
  frontWheelSize,
  nearByStoreCodes,
  pageNum,
  rearTireProductCode,
  rearWheelSize,
  searchOption,
  searchQuery,
  storeCode,
  vehicleInput
}) => ({
  errorPolicy: 'all',
  mapResponse: data => ({
    filters: data?.fitmentSearch?.staggeredWheelPackageSearch?.facets,
    numberOfPages:
      data?.fitmentSearch?.staggeredWheelPackageSearch?.pagination
        ?.numberOfPages,
    products: data?.fitmentSearch?.staggeredWheelPackageSearch?.results,
    sorts: data?.fitmentSearch?.staggeredWheelPackageSearch?.sorts,
    totalNumberOfResults:
      data?.fitmentSearch?.staggeredWheelPackageSearch?.pagination
        ?.totalNumberOfResults
  }),
  query: STAGGERED_WHEEL_PACKAGE_SEARCH,
  variables: {
    searchInput: {
      front: frontWheelSize,
      frontTireProductCode,
      nearByStoreCodes,
      pageNumber: pageNum,
      pageSize: PAGE_SIZE,
      rawQuery: searchQuery,
      rear: rearWheelSize,
      rearTireProductCode,
      searchOption
    },
    storeCode,
    vehicleInfo: vehicleInput
  }
});

const TIRE_SIZE_PRODUCT_SEARCH = gql`
  query TireSizeProductSearch(
    $searchInput: TireSizeSearchInput!
    $vehicleInfo: VehicleInput
    $storeCode: String!
  ) {
    productSearch {
      tireSize(
        searchInput: $searchInput
        vehicleInfo: $vehicleInfo
        storeCode: $storeCode
      ) {
        ...productSearchResult
      }
    }
  }
  ${productSearchResult}
`;

export const getTireSizeProductSearch = ({
  frontTireSize,
  nearByStoreCodes,
  pageNum,
  rearTireSize,
  searchQuery,
  storeCode,
  vehicleInput
}) => {
  const variables = {
    searchInput: {
      front: frontTireSize,
      nearByStoreCodes,
      pageNumber: pageNum || 0,
      pageSize: PAGE_SIZE,
      rawQuery: searchQuery
    },
    storeCode,
    vehicleInfo: vehicleInput
  };

  if (rearTireSize) {
    variables.searchInput.rear = rearTireSize;
  }

  return {
    //TODO: Remove this temporary hack
    mapResponse: data => ({
      filters: data?.productSearch?.tireSize?.facets,
      numberOfPages: data?.productSearch?.tireSize?.pagination?.numberOfPages,
      products: data?.productSearch?.tireSize?.results,
      sorts: data?.productSearch?.tireSize?.sorts,
      totalNumberOfResults:
        data?.productSearch?.tireSize?.pagination?.totalNumberOfResults
    }),

    query: TIRE_SIZE_PRODUCT_SEARCH,

    variables
  };
};

const WHEEL_SIZE_PRODUCT_SEARCH = gql`
  query WheelSizeProductSearch(
    $searchInput: WheelSizeSearchInput!
    $vehicleInfo: VehicleInput
    $storeCode: String!
  ) {
    productSearch {
      wheelSize(
        searchInput: $searchInput
        vehicleInfo: $vehicleInfo
        storeCode: $storeCode
      ) {
        ...productSearchResult
      }
    }
  }

  ${productSearchResult}
`;

export const getWheelSizeProductSearch = ({
  frontWheelSize,
  nearByStoreCodes,
  pageNum,
  rearWheelSize,
  searchQuery,
  storeCode,
  vehicleInput
}) => ({
  mapResponse: data => ({
    filters: data?.productSearch?.wheelSize?.facets,
    numberOfPages: data?.productSearch?.wheelSize?.pagination?.numberOfPages,
    products: data?.productSearch?.wheelSize?.results,
    sorts: data?.productSearch?.wheelSize?.sorts,
    totalNumberOfResults:
      data?.productSearch?.wheelSize?.pagination?.totalNumberOfResults
  }),
  query: WHEEL_SIZE_PRODUCT_SEARCH,
  variables: {
    searchInput: {
      front: frontWheelSize,
      nearByStoreCodes,
      pageNumber: pageNum || 0,
      pageSize: PAGE_SIZE,
      rawQuery: searchQuery,
      rear: rearWheelSize
    },
    storeCode,
    vehicleInfo: vehicleInput
  }
});

const FITMENT_WHEEL_PRODUCT_SEARCH = gql`
  query FitmentWheelProductSearch(
    $searchInput: StandardWheelSearchInput!
    $storeCode: String!
    $vehicleInfo: VehicleInput!
  ) {
    fitmentSearch(vehicleInfo: $vehicleInfo) {
      standardWheelSearch(searchInput: $searchInput, storeCode: $storeCode) {
        ...productSearchResult
      }
    }
  }

  ${productSearchResult}
`;

export const getFitmentWheelProductSearch = ({
  frontWheelSize,
  nearByStoreCodes,
  pageNum,
  searchOption,
  searchQuery,
  storeCode,
  vehicleInput
}) => ({
  mapResponse: data => ({
    filters: data?.fitmentSearch?.standardWheelSearch?.facets,
    numberOfPages:
      data?.fitmentSearch?.standardWheelSearch?.pagination?.numberOfPages,
    products: data?.fitmentSearch?.standardWheelSearch?.results,
    sorts: data?.fitmentSearch?.standardWheelSearch?.sorts,
    totalNumberOfResults:
      data?.fitmentSearch?.standardWheelSearch?.pagination?.totalNumberOfResults
  }),
  query: FITMENT_WHEEL_PRODUCT_SEARCH,
  variables: {
    searchInput: {
      nearByStoreCodes,
      pageNumber: pageNum,
      pageSize: PAGE_SIZE,
      rawQuery: searchQuery,
      searchOption,
      size: frontWheelSize
    },
    storeCode,
    vehicleInfo: vehicleInput
  }
});

const FITMENT_STAGGERED_WHEEL_PRODUCT_SEARCH = gql`
  query FitmentStaggeredWheelProductSearch(
    $vehicleInfo: VehicleInput!
    $searchInput: StaggeredWheelSearchInput!
    $storeCode: String!
  ) {
    fitmentSearch(vehicleInfo: $vehicleInfo) {
      staggeredWheelSearch(searchInput: $searchInput, storeCode: $storeCode) {
        ...productSearchResult
      }
    }
  }

  ${productSearchResult}
`;

export const getFitmentStaggeredWheelProductSearch = ({
  frontWheelSize,
  nearByStoreCodes,
  pageNum,
  rearWheelSize,
  searchOption,
  searchQuery,
  staggeredSearchType,
  storeCode,
  vehicleInput
}) => ({
  mapResponse: data => ({
    filters: data?.fitmentSearch?.staggeredWheelSearch?.facets,
    numberOfPages:
      data?.fitmentSearch?.staggeredWheelSearch?.pagination?.numberOfPages,
    products: data?.fitmentSearch?.staggeredWheelSearch?.results,
    sorts: data?.fitmentSearch?.staggeredWheelSearch?.sorts,
    totalNumberOfResults:
      data?.fitmentSearch?.staggeredWheelSearch?.pagination
        ?.totalNumberOfResults
  }),
  query: FITMENT_STAGGERED_WHEEL_PRODUCT_SEARCH,
  variables: {
    searchInput: {
      front: frontWheelSize,
      nearByStoreCodes,
      pageNumber: pageNum,
      pageSize: PAGE_SIZE,
      rawQuery: searchQuery,
      rear: rearWheelSize,
      searchOption,
      staggeredType: staggeredSearchType
    },
    storeCode,
    vehicleInfo: vehicleInput
  }
});

const PREBUILT_PACKAGE_SEARCH = gql`
  query fitmentSearch(
    $vehicleInfo: VehicleInput!
    $searchInput: PreBuiltPackageSearchInput!
    $storeCode: String!
  ) {
    fitmentSearch(vehicleInfo: $vehicleInfo) {
      preBuiltPackageSearch(searchInput: $searchInput, storeCode: $storeCode) {
        pagination {
          currentPage
          numberOfPages
          pageSize
          totalNumberOfResults
        }
        sorts {
          code
          name
          selected
        }
        results {
          product1 {
            ...productFragment
          }
          product2 {
            ...productFragment
          }
          stock {
            availabilityMessage
            availabilityMessageRank
            cityOfStore
            purchaseDecisionValue
            relativeStockCount
            relativeInventory {
              isAvailable
              relativeInventoryStatus
              stockMessagingCode
            }
            stockCount
            vendorsStockCount
            stockLevelStatus {
              code
            }
          }
          url
        }
        facets {
          ...facetsFragment
        }
      }
    }
  }
  ${productFragment}
  ${facetsFragment}
`;

export const getPrebuiltPackageSearch = ({
  frontTireSize,
  frontWheelSize,
  nearByStoreCodes,
  pageNum,
  searchOption,
  searchQuery,
  storeCode,
  vehicleInput
}) => ({
  mapResponse: data => ({
    filters: data?.fitmentSearch?.preBuiltPackageSearch?.facets,
    numberOfPages:
      data?.fitmentSearch?.preBuiltPackageSearch?.pagination?.numberOfPages,
    products: data?.fitmentSearch?.preBuiltPackageSearch?.results,
    sorts: data?.fitmentSearch?.preBuiltPackageSearch?.sorts,
    totalNumberOfResults:
      data?.fitmentSearch?.preBuiltPackageSearch?.pagination
        ?.totalNumberOfResults
  }),
  query: PREBUILT_PACKAGE_SEARCH,
  variables: {
    searchInput: {
      nearByStoreCodes,
      pageNumber: pageNum,
      pageSize: PACKAGE_PAGE_SIZE,
      rawQuery: searchQuery,
      searchOption,
      tireSize: frontTireSize,
      wheelSize: frontWheelSize
    },
    storeCode,
    vehicleInfo: vehicleInput
  }
});

export const CMS_BRAND_CONTENT = gql`
  query cmsBrandContent($id: String!) {
    cms {
      brandContent(id: $id) {
        contentSlots {
          name: id
          cmsComponents {
            ... on CmsHtmlComponent {
              html
            }
          }
        }
      }
    }
  }
`;
